import { useQuery } from '@apollo/client'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box, Button, Container, Link as MuiLink, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import DateStepper from '../../common/date/DateStepper'
import type { WithDateProps } from '../../common/date/withDate'
import withDate from '../../common/date/withDate'
import Loader from '../../common/loader/Loader'
import type { Campaign } from '../../common/types/campaigns'
import RestrictedView from '../auth/RestrictedView'
import { Reservation } from '../dailyInfo/data'
import Header from '../header/Header'
import { i18n } from '../index/index'
import Campaigns from './Campaigns'
import FrontpageAlert from '../OldDashboard/OldFrontPageAlert'
import type { Station } from '../station/stationTypes'
import withActiveStation from '../station/withActiveStation'
import type { ProgressSection } from './components/DashboardCard'
import DashboardCard from './components/DashboardCard'
import { fetchDashboardStats, fetchExpressCheckoutMenu } from './dashboardQueries'
import Reservations from './Reservations'

// ---------- Types ----------

type Props = WithDateProps & {
  activeStation: Station
}

type SalesData = {
  actual?: number
  target?: number
  actualLastUpdatedAt?: string
}

type SalesIncentiveData = {
  staffTarget?: number
  staffActual?: number
  expressCheckoutTarget?: number
  expressCheckoutActual?: number
  chainTarget?: number
  hasExpressCheckout?: boolean
}

type RestaurantMenuCategory = {
  id: string
  name: string
}

type RestaurantMenuPeriod = {
  categoryId: string
  periodName: string
  startDate: string
  endDate: string
}

type DashboardQueryVars = {
  stationId: string
  date: string
}

type DashboardQueryData = {
  dashboard?: {
    dailySales?: SalesData
    salesIncentive?: SalesIncentiveData
    averageFeelingMeter?: number
    feelingsCount?: number
    thumbsUp?: {
      target?: number
      actual?: number
    }
  }
  reservations?: Reservation[]
  campaigns?: Campaign[]
}

type ExpressCheckoutMenuData = {
  restaurantMenu?: {
    categories: RestaurantMenuCategory[]
  }
  restaurantMenuPeriods?: RestaurantMenuPeriod[]
}

// ---------- Constants ----------

const FEELING_MAX = 5

// ---------- Helpers ----------

const formatTime = (iso: string) => moment(iso).format('HH:mm')

// ---------- Sub-components ----------

const InfoLink: React.FC<{ to: string }> = ({ to }) => (
  <MuiLink
    component={Link}
    to={to}
    underline='none'
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      fontWeight: 600,
      color: 'success.main',
      fontSize: '0.875rem',
    }}
  >
    {i18n.t('dashboard.widget.infoLink')} <ArrowForwardIcon sx={{ fontSize: 16 }} />
  </MuiLink>
)

type FeelingDisplayProps = {
  feeling: number
  count: number
}

const FeelingDisplay: React.FC<FeelingDisplayProps> = ({ feeling, count }) => {
  const rounded = Math.round(feeling)
  const display = feeling > 0 ? Math.round(feeling * 10) / 10 : '-'

  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%' }}>
      <Stack direction='row' alignItems='center' spacing={2}>
        <img
          src={`/feeling-${rounded}.svg`}
          alt={i18n.t('dashboard.widget.feelingMeter.imageAlt')}
          style={{ width: 32, height: 32 }}
        />
        <Typography variant='body1' fontWeight={500}>
          {display}/{FEELING_MAX}
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary'>
        {i18n.t('dashboard.widget.feelingMeter.responses', { count })}
      </Typography>
    </Stack>
  )
}

// ---------- Main component ----------

const emptySales: SalesData = { actual: 0, target: 0 }
const emptyIncentive: SalesIncentiveData = {}

const Dashboard: React.FC<Props> = ({ activeStation, date, onDateChange }) => {
  const [alertOpen, setAlertOpen] = React.useState(true)
  const variables = useMemo<DashboardQueryVars>(
    () => ({
      stationId: activeStation.id,
      date,
    }),
    [activeStation.id, date],
  )

  const { data, loading, error } = useQuery<DashboardQueryData, DashboardQueryVars>(fetchDashboardStats, { variables })

  const dashboard = data?.dashboard
  const dailySales: SalesData = dashboard?.dailySales ?? emptySales
  const salesIncentive: SalesIncentiveData = dashboard?.salesIncentive ?? emptyIncentive
  const averageFeelingMeter = dashboard?.averageFeelingMeter ?? 0
  const reservations = data?.reservations ?? []
  const campaigns = data?.campaigns ?? []
  const feelingsCount = dashboard?.feelingsCount ?? 0
  const hasExpressCheckout = salesIncentive.hasExpressCheckout ?? false
  const thumbsUpTarget = dashboard?.thumbsUp?.target ?? 0
  const thumbsUpActual = dashboard?.thumbsUp?.actual ?? 0

  // Always fetch the express-checkout menu to show alerts about missing periods
  const { data: menuData } = useQuery<ExpressCheckoutMenuData, { stationId: string }>(fetchExpressCheckoutMenu, {
    variables: { stationId: activeStation.id },
  })
  const restaurantMenu = menuData?.restaurantMenu
  const restaurantMenuPeriods = menuData?.restaurantMenuPeriods ?? []

  const incentiveSections = useMemo<ProgressSection[]>(() => {
    const sections: ProgressSection[] = [
      {
        label: i18n.t('dashboard.widget.combinedIncentive.staff'),
        actual: salesIncentive.staffActual ?? 0,
        target: salesIncentive.staffTarget ?? 0,
        showValues: true,
      },
    ]
    if (hasExpressCheckout) {
      sections.push({
        label: i18n.t('dashboard.widget.combinedIncentive.expressCheckout'),
        actual: salesIncentive.expressCheckoutActual ?? 0,
        target: salesIncentive.expressCheckoutTarget ?? 0,
        showValues: true,
      })
    }
    return sections
  }, [salesIncentive, hasExpressCheckout])

  return (
    <Box component='main' sx={{ flexGrow: 1 }}>
      <Header title={i18n.t('dashboard.header')} />
      <Loader
        {...{
          loading,
          error,
        }}
      >
        <RestrictedView requiredPermission='view_dashboard'>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Container maxWidth='lg'>
              <Stack spacing={3}>
                <Typography variant='h6' component='h1' textAlign='center' fontWeight={700}>
                  {i18n.t('dashboard.header')}
                </Typography>
                <FrontpageAlert
                  date={date}
                  restaurantMenu={restaurantMenu}
                  restaurantMenuPeriods={restaurantMenuPeriods}
                  alertOpen={alertOpen}
                  closeAlert={() => setAlertOpen(false)}
                />
                <DateStepper date={date} onDateChange={onDateChange} />
                {!loading && !error && (
                  <Grid container spacing={3}>
                    {/* 1. Päivämyynti (Daily sales) */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <DashboardCard
                        title={i18n.t('dashboard.widget.daySales.title')}
                        subheader={
                          dailySales.actualLastUpdatedAt
                            ? i18n.t('dashboard.widget.daySales.updatedAt', {
                                lastUpdatedAt: formatTime(dailySales.actualLastUpdatedAt),
                              })
                            : undefined
                        }
                        sections={[
                          {
                            actual: dailySales.actual ?? 0,
                            target: dailySales.target ?? 0,
                            unit: '€',
                            showValues: true,
                          },
                        ]}
                        showTotalPercentage
                        footer={<InfoLink to={`/vuorokausijohtaminen/${date}`} />}
                      />
                    </Grid>

                    {/* 2. Aloitteellinen myynti (Combined incentive) */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <DashboardCard
                        title={i18n.t('dashboard.widget.combinedIncentive.title')}
                        sections={incentiveSections}
                        showTotalPercentage
                        footer={<InfoLink to={`/vuorokausijohtaminen/${date}`} />}
                      />
                    </Grid>

                    {/* 3. Ilarit (Thumbs up) */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <DashboardCard
                        title={i18n.t('dashboard.widget.thumbsUp.title')}
                        sections={[{ actual: thumbsUpActual, target: thumbsUpTarget, showValues: true }]}
                        showTotalPercentage
                        footer={<InfoLink to={`/vuorokausijohtaminen/${date}`} />}
                      />
                    </Grid>

                    {/* 4a. Pikakassa (Express checkout usage rate) - temporarily hidden until production data ready */}
                    {/* {hasExpressCheckout && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <DashboardCard
                          title={i18n.t('dashboard.widget.expressCheckoutUsage.title')}
                          sections={[
                            {
                              label: i18n.t('dashboard.widget.expressCheckoutUsage.yesterday'),
                              actual: 0,
                              target: 100,
                              showPercentage: true,
                            },
                            {
                              label: i18n.t('dashboard.widget.expressCheckoutUsage.monthToDate'),
                              actual: 0,
                              target: 100,
                              showPercentage: true,
                            },
                            {
                              label: i18n.t('dashboard.widget.expressCheckoutUsage.yearToDate'),
                              actual: 0,
                              target: 100,
                              showPercentage: true,
                            },
                          ]}
                          footer={
                            <Typography variant='body2'>
                              {i18n.t('dashboard.widget.target', { value: '25', unit: '%' })}
                            </Typography>
                          }
                        />
                      </Grid>
                    )} */}

                    {/* 4b. Mikä fiilis (Feelings meter) */}
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <DashboardCard
                        title={i18n.t('dashboard.widget.feelingMeter.title')}
                        header={
                          <Button
                            component={Link}
                            to={`/tyontekijakortti/fiilismittari/${date}`}
                            variant='contained'
                            color='success'
                            size='small'
                            sx={{ fontWeight: 600 }}
                          >
                            {i18n.t('dashboard.widget.feelingMeter.addFeeling')}
                          </Button>
                        }
                        subheader={<FeelingDisplay feeling={averageFeelingMeter} count={feelingsCount} />}
                        sections={[{ actual: averageFeelingMeter, target: FEELING_MAX }]}
                        footer={
                          <>
                            <RestrictedView requiredPermission='manage_ownStation'>
                              <Stack direction='row' justifyContent='flex-end'>
                                <InfoLink to='/raportointi/fiilismittaripalautteet' />
                              </Stack>
                            </RestrictedView>
                          </>
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                <Reservations rows={reservations} stationId={activeStation.id} date={date} />
                <Campaigns campaigns={campaigns} stationId={activeStation.id} />
              </Stack>
            </Container>
          </LocalizationProvider>
        </RestrictedView>
      </Loader>
    </Box>
  )
}

export default withDate()(withActiveStation(Dashboard))
